import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Loader2, Copy, CheckCircle2, RefreshCw, Calendar as CalendarIcon, Hash, Image as ImageIcon, ShieldCheck, AlertTriangle, Download, Sparkles, Link as LinkIcon } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const CITIES = ['International', 'London', 'Manchester', 'Chandigarh', 'Delhi', 'Toronto', 'Vancouver'];

const CopyBtn = ({ text, label, testId }) => {
  const [copied, setCopied] = useState(false);
  const handle = async () => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success(`${label} copied`);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error('Clipboard blocked — please select + copy manually');
    }
  };
  return (
    <button type="button" onClick={handle} className={copied ? 'pe-btn pe-btn-ghost' : 'pe-btn pe-btn-ghost'} data-testid={testId}>
      {copied ? <CheckCircle2 className="h-3.5 w-3.5" style={{ color: 'var(--pe-success)' }} /> : <Copy className="h-3.5 w-3.5" />}
      {copied ? 'Copied' : `Copy ${label}`}
    </button>
  );
};

export const AdminFBCalendar = () => {
  const { token } = useAuth();
  const [cal, setCal] = useState(null);
  const [cost, setCost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [polling, setPolling] = useState(false);
  const [activeCat, setActiveCat] = useState('market-insight');
  const [activeCity, setActiveCity] = useState('International');
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [c, r] = await Promise.all([
        axios.get(`${API_URL}/api/admin/fb-calendar`, auth),
        axios.get(`${API_URL}/api/admin/fb-calendar/cost-report`, auth),
      ]);
      setCal(c.data?.exists ? c.data : null);
      setCost(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load calendar');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const generate = async () => {
    if (!window.confirm('Generate a new 30-day calendar? This replaces the current one. Uses cached LLM calls when possible — typical cost ~0.3-0.6 credits.')) return;
    setGenerating(true);
    try {
      await axios.post(`${API_URL}/api/admin/fb-calendar/generate`, {}, auth);
      toast.info('Generation queued — polling for completion...');
      setPolling(true);
      const start = Date.now();
      const maxMs = 4 * 60 * 1000;
      const tick = async () => {
        try {
          const r = await axios.get(`${API_URL}/api/admin/fb-calendar`, auth);
          if (r.data?.status === 'ok' && r.data?.total_posts > 0) {
            setCal(r.data);
            setGenerating(false);
            setPolling(false);
            toast.success(`${r.data.total_posts} posts generated`);
            loadAll();
            return;
          }
          if (Date.now() - start > maxMs) {
            setGenerating(false);
            setPolling(false);
            toast.warning('Still running — refresh in a moment.');
            return;
          }
          setTimeout(tick, 10000);
        } catch {
          setTimeout(tick, 10000);
        }
      };
      setTimeout(tick, 10000);
    } catch (e) {
      setGenerating(false);
      toast.error(e.response?.data?.detail || 'Generate failed');
    }
  };

  const downloadJSON = () => {
    if (!cal) return;
    const blob = new Blob([JSON.stringify(cal, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `shopperden-fb-calendar-${cal.generated_at?.slice(0, 10) || 'latest'}.json`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('JSON downloaded');
  };

  const currentCategory = useMemo(
    () => (cal?.categories || []).find(c => c.category === activeCat),
    [cal, activeCat],
  );

  // Per-post image cache + tracker state — keys are `${category_id}-p${index}`
  const [postImages, setPostImages] = useState({});
  const [imageBusy, setImageBusy] = useState({});
  const [trackerBusy, setTrackerBusy] = useState({});

  const loadImages = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/admin/fb-calendar/images`, auth);
      const map = {};
      (r.data?.items || []).forEach(it => { map[it.post_key] = it.image_url; });
      setPostImages(map);
    } catch (e) { /* silent */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { loadImages(); }, [loadImages]);

  const generateImage = async (categoryId, idx) => {
    const key = `${categoryId}-p${idx}`;
    setImageBusy(b => ({ ...b, [key]: true }));
    try {
      const r = await axios.post(`${API_URL}/api/admin/fb-calendar/image/${categoryId}/${idx}`, {}, auth);
      setPostImages(p => ({ ...p, [key]: r.data.image_url }));
      toast.success(r.data.from_cache ? 'Image loaded from cache (0 credits)' : `Image generated · ~${r.data.est_credits} credits`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Image generation failed');
    } finally {
      setImageBusy(b => ({ ...b, [key]: false }));
    }
  };

  const registerTracker = async (categoryId, idx, city) => {
    const key = `${categoryId}-p${idx}-${city}`;
    setTrackerBusy(b => ({ ...b, [key]: true }));
    const fbUrl = window.prompt('Optional: paste the public Facebook post URL where you shared this (or leave blank).');
    try {
      const r = await axios.post(`${API_URL}/api/admin/fb-tracker/register`, {
        category_id: categoryId, post_index: idx, city, fb_post_url: fbUrl || '',
      }, auth);
      const link = r.data.tracking_url;
      try { await navigator.clipboard.writeText(link); } catch {}
      toast.success(`Tracking link copied · ${link}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Tracker registration failed');
    } finally {
      setTrackerBusy(b => ({ ...b, [key]: false }));
    }
  };

  const postCopyFor = (post) => {
    if (activeCity === 'International') {
      return {
        body: post.body,
        hashtags: post.hashtags || [],
      };
    }
    const variant = (post.regional_variants || {})[activeCity] || {};
    return {
      body: variant.body || post.body,
      hashtags: variant.hashtags || post.hashtags || [],
    };
  };

  if (loading) {
    return (
      <div className="pe-card text-center" style={{ padding: 'var(--pe-space-xl)' }} data-testid="fb-cal-loading">
        <Loader2 className="h-8 w-8 animate-spin mx-auto" style={{ color: 'var(--pe-gold)' }} />
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="admin-fb-calendar">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="pe-label mb-1">Facebook Authority Funnel</p>
          <h2 className="pe-h2">Content Calendar</h2>
          <p className="pe-body mt-1" style={{ maxWidth: '640px' }}>
            30 posts · 180 regional variants · Meta-compliant. Pick a city, copy, paste into Facebook manually.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={loadAll} className="pe-btn pe-btn-ghost" data-testid="fb-cal-refresh">
            <RefreshCw className="h-3.5 w-3.5" /> Refresh
          </button>
          {cal && (
            <button type="button" onClick={downloadJSON} className="pe-btn pe-btn-ghost" data-testid="fb-cal-download">
              <Download className="h-3.5 w-3.5" /> Download JSON
            </button>
          )}
          <button type="button" onClick={generate} disabled={generating} className="pe-btn pe-btn-primary" data-testid="fb-cal-generate">
            {generating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CalendarIcon className="h-3.5 w-3.5" />}
            {generating ? (polling ? 'Generating…' : 'Queued…') : (cal ? 'Regenerate' : 'Generate now')}
          </button>
        </div>
      </div>

      {/* Cost + status bar */}
      {cost && (
        <div className="pe-card flex flex-wrap items-center gap-4" style={{ padding: 'var(--pe-space-md)' }} data-testid="fb-cal-cost">
          <div className="flex-1 min-w-[200px]">
            <p className="pe-kpi-label">7-day credit spend</p>
            <p className="pe-kpi-value pe-kpi-value-gold">{cost.total_est_credits?.toFixed(3) || '0.000'} credits</p>
          </div>
          <div className="flex-1 min-w-[200px]">
            <p className="pe-kpi-label">Sequences this week</p>
            <p className="pe-kpi-value">{cost.sequences?.length || 0}</p>
          </div>
          <div className="flex-1 min-w-[220px]">
            <p className="pe-kpi-label">Alert threshold</p>
            <p className="pe-kpi-value" style={{ color: cost.over_threshold ? 'var(--pe-error)' : 'var(--pe-text-soft)' }}>
              {cost.total_est_credits?.toFixed(3) || '0.000'} / {cost.alert_threshold?.toFixed(1) || '5.0'}
            </p>
          </div>
          {cal?.generated_at && (
            <div className="flex-1 min-w-[220px]">
              <p className="pe-kpi-label">Last generated</p>
              <p className="pe-body" style={{ color: 'var(--pe-text)', fontWeight: 600 }}>{new Date(cal.generated_at).toLocaleString()}</p>
            </div>
          )}
        </div>
      )}

      {!cal ? (
        <div className="pe-card text-center" style={{ padding: 'var(--pe-space-xl)' }} data-testid="fb-cal-empty">
          <CalendarIcon className="h-8 w-8 mx-auto mb-3" style={{ color: 'var(--pe-gold)' }} />
          <h3 className="pe-h3">No calendar yet</h3>
          <p className="pe-body mt-2">Click <b style={{ color: 'var(--pe-text)' }}>Generate now</b> to draft 30 posts + 180 regional variants.</p>
        </div>
      ) : (
        <>
          {/* Category tabs + City tabs */}
          <div className="pe-card space-y-4" style={{ padding: 'var(--pe-space-md)' }}>
            <div>
              <p className="pe-kpi-label mb-2">Category</p>
              <div className="flex flex-wrap gap-2" data-testid="fb-cal-category-tabs">
                {(cal.categories || []).map(c => (
                  <button
                    key={c.category}
                    type="button"
                    onClick={() => setActiveCat(c.category)}
                    className={activeCat === c.category ? 'pe-btn pe-btn-primary' : 'pe-btn pe-btn-ghost'}
                    data-testid={`fb-cat-${c.category}`}
                  >
                    {c.label || c.category} · {c.posts?.length || 0}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <p className="pe-kpi-label mb-2">City variant</p>
              <div className="flex flex-wrap gap-2" data-testid="fb-cal-city-tabs">
                {CITIES.map(city => (
                  <button
                    key={city}
                    type="button"
                    onClick={() => setActiveCity(city)}
                    className={activeCity === city ? 'pe-btn pe-btn-primary' : 'pe-btn pe-btn-ghost'}
                    data-testid={`fb-city-${city}`}
                  >
                    {city}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Posts grid */}
          <div className="space-y-3" data-testid="fb-cal-posts">
            {(currentCategory?.posts || []).map((post, idx) => {
              const variant = postCopyFor(post);
              const fullShare = `${variant.body}\n\n${(variant.hashtags || []).join(' ')}\n\n${post.cta_url || ''}`;
              return (
                <div key={post.id || `fb-post-${idx}`} className="pe-card" style={{ padding: 'var(--pe-space-lg)' }} data-testid={`fb-post-${idx}`}>
                  <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                    <div className="flex-1 min-w-[240px]">
                      <p className="pe-kpi-label" style={{ color: 'var(--pe-gold)' }}>Post #{post.id || idx + 1} · {activeCity}</p>
                      <h4 className="pe-h3 mt-1" style={{ fontSize: '1.125rem' }}>{post.title}</h4>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <CopyBtn text={variant.body} label="body" testId={`fb-copy-body-${idx}`} />
                      <CopyBtn text={(variant.hashtags || []).join(' ')} label="hashtags" testId={`fb-copy-hash-${idx}`} />
                      <CopyBtn text={fullShare} label="full post" testId={`fb-copy-full-${idx}`} />
                    </div>
                  </div>
                  <p className="pe-body" style={{ whiteSpace: 'pre-wrap' }}>{variant.body}</p>
                  <div className="mt-3 flex flex-wrap gap-1" data-testid={`fb-post-hashtags-${idx}`}>
                    {(variant.hashtags || []).map((h, i) => (
                      <span key={`${post.id || idx}-tag-${h || i}`} className="pe-chip pe-chip-gold">{h}</span>
                    ))}
                  </div>
                  {post.image_description && (
                    <div className="mt-3 pe-surface-2" style={{ padding: '10px 14px', borderTop: '1px solid var(--pe-border)' }}>
                      <div className="flex items-center justify-between gap-2 mb-1.5 flex-wrap">
                        <p className="pe-kpi-label" style={{ marginBottom: 0 }}>
                          <ImageIcon className="h-3 w-3 inline mr-1" /> Image prompt
                        </p>
                        <button
                          type="button"
                          onClick={() => generateImage(activeCat, idx)}
                          disabled={imageBusy[`${activeCat}-p${idx}`]}
                          className="pe-btn pe-btn-ghost"
                          style={{ padding: '0.25rem 0.6rem', fontSize: '0.625rem' }}
                          data-testid={`fb-gen-img-${idx}`}
                        >
                          {imageBusy[`${activeCat}-p${idx}`] ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
                          {postImages[`${activeCat}-p${idx}`] ? 'Regenerate' : 'Generate Image'}
                        </button>
                      </div>
                      <p className="pe-body" style={{ fontSize: '0.8125rem', fontStyle: 'italic' }}>{post.image_description}</p>
                      {postImages[`${activeCat}-p${idx}`] && (
                        <div className="mt-2" data-testid={`fb-post-image-${idx}`}>
                          <img
                            src={`${API_URL}${postImages[`${activeCat}-p${idx}`]}`}
                            alt={`AI image for post ${idx + 1}`}
                            style={{ width: '100%', maxWidth: '480px', display: 'block', border: '1px solid var(--pe-border)' }}
                          />
                          <a
                            href={`${API_URL}${postImages[`${activeCat}-p${idx}`]}`}
                            download
                            className="pe-btn pe-btn-ghost mt-2"
                            style={{ padding: '0.25rem 0.6rem', fontSize: '0.625rem' }}
                          >
                            <Download className="h-3 w-3" /> Download
                          </a>
                        </div>
                      )}
                    </div>
                  )}
                  <div className="mt-3 flex flex-wrap gap-2 items-center">
                    {post.cta_url && (
                      <p className="pe-body" style={{ fontSize: '0.75rem', color: 'var(--pe-text-muted)', flex: '1 1 240px' }}>
                        CTA URL: <code style={{ color: 'var(--pe-gold)' }}>{post.cta_url}</code>
                      </p>
                    )}
                    <button
                      type="button"
                      onClick={() => registerTracker(activeCat, idx, activeCity)}
                      disabled={trackerBusy[`${activeCat}-p${idx}-${activeCity}`]}
                      className="pe-btn pe-btn-ghost"
                      style={{ padding: '0.35rem 0.7rem', fontSize: '0.625rem' }}
                      data-testid={`fb-track-${idx}`}
                      title="Get a UTM-tagged tracking URL for this post + city, copied to clipboard"
                    >
                      {trackerBusy[`${activeCat}-p${idx}-${activeCity}`] ? <Loader2 className="h-3 w-3 animate-spin" /> : <LinkIcon className="h-3 w-3" />}
                      Get tracking URL
                    </button>
                  </div>
                </div>
              );
            })}
            {(currentCategory?.posts || []).length === 0 && (
              <div className="pe-card text-center" style={{ padding: 'var(--pe-space-lg)' }}>
                <p className="pe-body">No posts in this category yet.</p>
              </div>
            )}
          </div>

          {/* Meta compliance guide */}
          {cal.meta_compliance_html && (
            <div className="pe-card" style={{ padding: 'var(--pe-space-lg)' }} data-testid="fb-cal-compliance">
              <div className="flex items-center gap-2 mb-3">
                <ShieldCheck className="h-4 w-4" style={{ color: 'var(--pe-gold)' }} />
                <h3 className="pe-h3" style={{ fontSize: '1rem', margin: 0 }}>Manual Sharing Guide</h3>
              </div>
              <div className="pe-body" style={{ fontSize: '0.8125rem', lineHeight: 1.7 }} dangerouslySetInnerHTML={{ __html: cal.meta_compliance_html }} />
            </div>
          )}

          {/* Safety callout */}
          <div className="pe-card flex items-start gap-3" style={{ padding: 'var(--pe-space-md)', borderColor: 'var(--pe-warning)' }}>
            <AlertTriangle className="h-5 w-5 flex-shrink-0 mt-0.5" style={{ color: 'var(--pe-warning)' }} />
            <div className="pe-body" style={{ fontSize: '0.8125rem' }}>
              <b style={{ color: 'var(--pe-text)' }}>Meta account safety:</b> never share more than 4 times per day from one account, wait 20-30 min between same-city group posts, tweak 1 word in the hook per share, always answer comments within 2 hours. Follow the guide above to stay off Meta&apos;s anti-spam triggers.
            </div>
          </div>

          {/* Hashtag reference panel */}
          <div className="pe-card" style={{ padding: 'var(--pe-space-md)' }} data-testid="fb-cal-hashtag-discipline">
            <div className="flex items-center gap-2 mb-2">
              <Hash className="h-4 w-4" style={{ color: 'var(--pe-gold)' }} />
              <b className="pe-body" style={{ color: 'var(--pe-text)' }}>Hashtag discipline</b>
            </div>
            <p className="pe-body" style={{ fontSize: '0.8125rem' }}>
              3-5 hashtags per post · one city tag · no generic spam (#love #follow #instagood).
              Rotate the non-city tags each week to avoid Meta&apos;s repeat-hashtag detection.
            </p>
          </div>
        </>
      )}
    </div>
  );
};

export default AdminFBCalendar;
